// Slepen in een raster (feat/vandaag-raster): de bediening die de bord-editor (kiosk-ui GridEdit)
// en Vandaag in de ouderapp (apps/web HomeGrid) delen. Zonder framework: één functie die bij het
// neerzetten van muis, pen of vinger start, de beweging omrekent naar hele vakken en bij loslaten
// zegt of de zet moet blijven. Pointer Events waar ze bestaan (ouderapp), anders touch en muis
// (iOS 12 kent geen Pointer Events). Escape, een tweede vinger of het venster verlaten breekt af.
// In het Arabisch telt de kolom vanaf rechts. De rasterlogica zelf (plaatsen, botsen) staat in
// @amumin/domain/grid.

export type GridDragVia = 'pointer' | 'touch' | 'mouse';

export interface GridDragOptions {
  /** Het rasterelement: de vakmaat volgt uit zijn grootte (ook geschaald, transform: scale). */
  el: HTMLElement;
  cols: number;
  rows: number;
  /** Ruimte tussen de vakken in px (ongeschaald). */
  gap: number;
  /** Startpunt (clientX, clientY). */
  x: number;
  y: number;
  via: GridDragVia;
  rtl: boolean;
  /** Een nieuwe stap in hele vakken t.o.v. het begin (alleen als er echt gesleept wordt). */
  onStep: (dc: number, dr: number) => void;
  /** Klaar: save = losgelaten (false = afgebroken); moved = er is echt gesleept. */
  onEnd: (save: boolean, moved: boolean) => void;
  /** Meescrollen bij de rand van het venster (een lange pagina, zoals Vandaag). */
  autoScroll?: boolean | undefined;
}

/** Bestaan Pointer Events hier? (ouderapp ja; iOS 12 nee.) */
export const hasPointerEvents = typeof window !== 'undefined' && 'PointerEvent' in window;

const EDGE = 72;

/** Begin met slepen; geeft een functie die het slepen afbreekt (zonder opslaan). */
export function startGridDrag(o: GridDragOptions): () => void {
  const rect = o.el.getBoundingClientRect();
  const scale = o.el.offsetWidth > 0 ? rect.width / o.el.offsetWidth : 1;
  const pitchX = (rect.width + o.gap * scale) / o.cols;
  const pitchY = (rect.height + o.gap * scale) / o.rows;
  const scroll0 = window.pageYOffset;
  let active = false;
  let done = false;
  let lastX = o.x,
    lastY = o.y;
  // NaN: de eerste echte beweging geeft altijd een stap (ook als die nog in hetzelfde vak is).
  let stepC = NaN,
    stepR = NaN;
  let raf = 0;

  const update = (cx: number, cy: number) => {
    lastX = cx;
    lastY = cy;
    const dx = cx - o.x,
      dy = cy - o.y + (window.pageYOffset - scroll0);
    if (!active && Math.abs(dx) + Math.abs(dy) < 6) return;
    active = true;
    const dc = Math.round((o.rtl ? -dx : dx) / pitchX);
    const dr = Math.round(dy / pitchY);
    if (dc === stepC && dr === stepR) return;
    stepC = dc;
    stepR = dr;
    o.onStep(dc, dr);
  };
  const tick = () => {
    if (done) return;
    if (active) {
      const by = lastY < EDGE ? -10 : lastY > window.innerHeight - EDGE ? 10 : 0;
      if (by !== 0) {
        window.scrollBy(0, by);
        update(lastX, lastY);
      }
    }
    raf = window.requestAnimationFrame(tick);
  };

  const finish = (save: boolean) => {
    if (done) return;
    done = true;
    window.removeEventListener('pointermove', onPointer);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', abort);
    window.removeEventListener('touchmove', onTouch);
    window.removeEventListener('touchend', onTouchEnd);
    window.removeEventListener('touchcancel', abort);
    window.removeEventListener('mousemove', onMouse);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('keydown', onKey, true);
    window.removeEventListener('blur', abort);
    if (raf) window.cancelAnimationFrame(raf);
    o.onEnd(save, active);
  };
  const onPointer = (e: PointerEvent) => update(e.clientX, e.clientY);
  const onPointerUp = (e: PointerEvent) => {
    update(e.clientX, e.clientY);
    finish(true);
  };
  const onTouch = (e: TouchEvent) => {
    if (e.touches.length !== 1) {
      finish(false);
      return;
    }
    e.preventDefault();
    const t = e.touches[0] as Touch;
    update(t.clientX, t.clientY);
  };
  const onTouchEnd = (e: TouchEvent) => {
    const p = e.changedTouches[0];
    if (p) update(p.clientX, p.clientY);
    finish(true);
  };
  const onMouse = (e: MouseEvent) => {
    e.preventDefault();
    update(e.clientX, e.clientY);
  };
  const onMouseUp = (e: MouseEvent) => {
    update(e.clientX, e.clientY);
    finish(true);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    finish(false);
  };
  const abort = () => finish(false);

  if (o.via === 'pointer') {
    window.addEventListener('pointermove', onPointer);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', abort);
  } else if (o.via === 'touch') {
    window.addEventListener('touchmove', onTouch, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', abort);
  } else {
    window.addEventListener('mousemove', onMouse);
    window.addEventListener('mouseup', onMouseUp);
  }
  window.addEventListener('keydown', onKey, true);
  window.addEventListener('blur', abort);
  if (o.autoScroll) raf = window.requestAnimationFrame(tick);
  return abort;
}

/**
 * De handlers om slepen te starten op een element (kader, greep): Pointer Events waar ze bestaan,
 * anders touch en muis. `begin` krijgt het startpunt en de soort invoer.
 */
export function gridDragStart(begin: (x: number, y: number, via: GridDragVia) => void) {
  return hasPointerEvents
    ? {
        onPointerDown: (e: PointerEvent) => {
          if (e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          begin(e.clientX, e.clientY, 'pointer');
        },
      }
    : {
        onTouchStart: (e: TouchEvent) => {
          if (e.touches.length !== 1) return;
          e.stopPropagation();
          const t = e.touches[0] as Touch;
          begin(t.clientX, t.clientY, 'touch');
        },
        onMouseDown: (e: MouseEvent) => {
          if (e.button !== 0) return;
          e.preventDefault();
          e.stopPropagation();
          begin(e.clientX, e.clientY, 'mouse');
        },
      };
}

/** Knoppen op een kader: geen slepen starten. */
export const gridDragStill = {
  onPointerDown: (e: Event) => e.stopPropagation(),
  onTouchStart: (e: Event) => e.stopPropagation(),
  onMouseDown: (e: Event) => e.stopPropagation(),
};
